import { formatDatePreview } from "./formatPreview";
import type {
  DateOrderOption,
  DateSeparatorOption,
  DayToken,
  MonthToken,
  WeekdayToken,
  YearToken,
} from "./patternBuilder";
import type {
  CurrencySymbolPositionOption,
  NegativeNumbersOption,
  NullDisplayOption,
  NumberScaleOption,
  SelectOption,
  ZeroDisplayOption,
} from "./types";

export const NEGATIVE_NUMBERS_OPTIONS: ReadonlyArray<SelectOption<NegativeNumbersOption>> = [
  { value: "parentheses", label: "Parentheses" },
  { value: "minus", label: "Minus sign" },
  { value: "absolute", label: "Absolute value" },
];

export const NUMBER_SCALE_OPTIONS: ReadonlyArray<SelectOption<NumberScaleOption>> = [
  { value: "as_is", label: "As is" },
  { value: "thousands", label: "Thousands" },
];

export const CURRENCY_SYMBOL_POSITION_OPTIONS: ReadonlyArray<
  SelectOption<CurrencySymbolPositionOption>
> = [
  { value: "left", label: "Left" },
  { value: "left_space", label: "Left with space" },
  { value: "right", label: "Right" },
  { value: "right_space", label: "Right with space" },
];

export const ZERO_DISPLAY_OPTIONS: ReadonlyArray<SelectOption<ZeroDisplayOption>> = [
  { value: "number", label: "Number (0)" },
  { value: "em_dash", label: "Em-dash (\u2014)" },
  { value: "en_dash", label: "En-dash (\u2013)" },
  { value: "blank", label: "Blank" },
  { value: "na", label: "N/A" },
];

export const NULL_DISPLAY_OPTIONS: ReadonlyArray<SelectOption<NullDisplayOption>> = [
  { value: "number", label: "Number (0)" },
  { value: "em_dash", label: "Em-dash (\u2014)" },
  { value: "en_dash", label: "En-dash (\u2013)" },
  { value: "blank", label: "Blank" },
  { value: "na", label: "N/A" },
];

/** Default date-fns pattern ("June 01, 2026" — the ticket's June / 01 /
 * yyyy defaults). Keep in sync with `DEFAULT_PATTERN` in
 * finpub-data-service (`modules/query/date_format.py`). */
export const DEFAULT_DATE_FORMAT_PATTERN = "MMMM dd, yyyy";

// -- Date-format builder option lists -----------------------------------------
//
// The date format is composed from an order + per-part token styles +
// weekday toggle + separator (see `patternBuilder.ts`). Token option labels
// render the token against the sample date so each dropdown reads like the
// preview: MMMM -> "June", dd -> "01", yy -> "26", ...

const tokenOption = <T extends string>(token: T): SelectOption<T> => ({
  value: token,
  label: formatDatePreview(token),
});

export const DATE_ORDER_OPTIONS: ReadonlyArray<SelectOption<DateOrderOption>> = [
  { value: "month_day_year", label: "Month Day Year" },
  { value: "day_month_year", label: "Day Month Year" },
  { value: "year_month_day", label: "Year Month Day" },
  { value: "month_year", label: "Month Year" },
  { value: "day_year", label: "Day Year" },
  { value: "year_only", label: "Year only" },
];

export const MONTH_TOKEN_OPTIONS: ReadonlyArray<SelectOption<MonthToken>> = [
  tokenOption("MMMM"),
  tokenOption("MMM"),
  tokenOption("MM"),
  tokenOption("M"),
];

export const DAY_TOKEN_OPTIONS: ReadonlyArray<SelectOption<DayToken>> = [
  tokenOption("dd"),
  tokenOption("d"),
];

export const YEAR_TOKEN_OPTIONS: ReadonlyArray<SelectOption<YearToken>> = [
  tokenOption("yyyy"),
  tokenOption("yy"),
];

export const WEEKDAY_TOKEN_OPTIONS: ReadonlyArray<SelectOption<WeekdayToken>> = [
  tokenOption("EEEE"),
  tokenOption("EEE"),
];

export const DATE_SEPARATOR_OPTIONS: ReadonlyArray<SelectOption<DateSeparatorOption>> = [
  { value: "comma", label: "Comma (,)" },
  { value: "slash", label: "Slash (/)" },
  { value: "dash", label: "Dash (-)" },
  { value: "space", label: "None" },
];
